import { all, call, put, takeEvery } from 'redux-saga/effects';
import { getRequest, postRequest } from '../../../helpers/axiosClient';
import { AESEncrypt } from '../../../helpers/Crypto';
import actions from './actions';
import adminActions from '../../Admin/Home/redux/actions';
import { storageSetItem, logoutUserIdle, storageRemoveItem, storageGetItem } from '../../../helpers/utilities';
import { tokenValidityMs } from '../../../setup/desktopApi';

export function* register(params) {
    try {
        const response = yield call(() => postRequest('user/signup', params.registerData));
        yield put({
            type: actions.REGISTER_SUCCESS,
            payload: response.data,
        });
    } catch (error) {
        yield put({ type: actions.REGISTER_FAILURE, message: error?.response?.data });
        return error;
    }
}

export function* login(params) {
    try {
        const deviceToken = storageGetItem("deviceToken");
        let response = '';
        if (deviceToken && deviceToken !== '') {
            response = yield call(() => postRequest(`user/login/${deviceToken}`, params.loginData));
        } else {
            response = yield call(() => postRequest(`user/login/null`, params.loginData));
        }

        storageSetItem('token', response.data.token);
        storageSetItem('businessType', response?.data?.businessType);
        storageSetItem('isPaying', true);
        storageSetItem('isNewUser', response?.data?.isNewUser);
        storageSetItem('event', response?.data?.event);
        storageSetItem('roleId', response?.data?.roleId);
        storageSetItem('languageId', 1);


        yield put({
            type: actions.LOGIN_SUCCESS,
            payload: response.data,
        });

        let roleResponse = yield call(() => getRequest(`role/${response?.data?.roleId}`));
        yield put({
            type: actions.ROLE_MANAGEMENT_SUCCESS,
            payload: roleResponse.data,
        });

        storageSetItem('roleName', AESEncrypt(roleResponse?.data?.name));
        // Cache the role so the menus/permissions survive offline (the Wrapper's
        // on-mount role refresh can't reach role/{id} without a connection).
        try { storageSetItem('roleData', JSON.stringify(roleResponse.data)); } catch (e) { /* ignore */ }

        let businessResponse = yield call(() => getRequest('businessProfile'));
        yield put({
            type: adminActions.BUSINESS_DETAILS_SUCCESS,
            payload: businessResponse.data,
        });

        let subResponse = yield call(() => getRequest('currentSubscription'));
        yield put({
            type: adminActions.SUBSCRIPTION_DETAILS_SUCCESS,
            payload: subResponse.data,
        });

        let paraResponse = yield call(() => getRequest(`business-parameters/${businessResponse.data.businessId}`));
        yield put({
            type: adminActions.PARAMETER_DETAILS_SUCCESS,
            payload: paraResponse.data,
        });

        // Desktop only: cache the full session (encrypted) so the user can sign in
        // offline later. Wrapped so a caching failure NEVER fails the online login.
        try {
            if (window.electron && window.electron.auth && window.electron.auth.seed) {
                yield call(() => window.electron.auth.seed({
                    email: params.loginData.email,
                    password: params.loginData.password,
                    token: response.data.token,
                    validityMs: tokenValidityMs,
                    profile: {
                        businessType: response?.data?.businessType,
                        isNewUser: response?.data?.isNewUser,
                        event: response?.data?.event,
                        roleId: response?.data?.roleId,
                        languageId: 1,
                        roleNameEnc: AESEncrypt(roleResponse?.data?.name),
                        role: roleResponse.data,
                        businessProfile: businessResponse.data,
                        subscription: subResponse.data,
                        parameters: paraResponse.data,
                        businessId: businessResponse?.data?.businessId,
                    },
                }));
            }
        } catch (seedErr) {
            // Offline login is a convenience; ignore caching errors.
        }

    } catch (error) {
        yield put({ type: actions.LOGIN_FAILURE, message: error?.response?.data });
        return error
    }
}

/**
 * Offline login: verify the typed password against the encrypted vault (main
 * process) and restore the cached session — token + the post-login cascade
 * (role/permissions, business profile, subscription, parameters) — into
 * localStorage and redux, mirroring the online login result.
 */
export function* offlineLogin(params) {
    try {
        const res = yield call(() => window.electron.auth.offlineLogin(params.loginData));
        if (!res || !res.ok) {
            const reasons = {
                'no-cached-credentials': 'No offline login yet. Connect to the internet and log in once.',
                'invalid-credentials': 'Incorrect password for offline login.',
                'no-cached-session': 'No saved session found. Please log in online once.',
                'session-expired': 'Your offline session has expired. Please connect and log in.',
            };
            const message = { error: { message: reasons[res && res.reason] || 'Offline login failed.' } };
            yield put({ type: actions.LOGIN_FAILURE, message });
            return;
        }

        const { token } = res;
        const p = res.profile || {};
        storageSetItem('token', token);
        storageSetItem('businessType', p.businessType);
        storageSetItem('isPaying', true);
        storageSetItem('isNewUser', p.isNewUser);
        storageSetItem('event', p.event);
        storageSetItem('roleId', p.roleId);
        storageSetItem('languageId', p.languageId || 1);
        if (p.roleNameEnc) storageSetItem('roleName', p.roleNameEnc);

        yield put({ type: actions.LOGIN_SUCCESS, payload: { token, event: p.event } });
        if (p.role) yield put({ type: actions.ROLE_MANAGEMENT_SUCCESS, payload: p.role });
        if (p.businessProfile) yield put({ type: adminActions.BUSINESS_DETAILS_SUCCESS, payload: p.businessProfile });
        if (p.subscription) yield put({ type: adminActions.SUBSCRIPTION_DETAILS_SUCCESS, payload: p.subscription });
        if (p.parameters) yield put({ type: adminActions.PARAMETER_DETAILS_SUCCESS, payload: p.parameters });
    } catch (error) {
        yield put({ type: actions.LOGIN_FAILURE, message: { error: { message: 'Offline login failed.' } } });
        return error;
    }
}

export function* changePassword(params) {
    try {
        const response = yield call(() => postRequest('user/changePassword', params.changePasswordData));
        yield put({
            type: actions.CHANGE_PASSWORD_SUCCESS,
            payload: response.data,
        });
        logoutUserIdle();
    } catch (error) {
        yield put({ type: actions.CHANGE_PASSWORD_FAILURE, message: error?.response?.data });
        return error;
    }
}

export function* verifyPassword(params) {
    try {
        const response = yield call(() => postRequest('user/verifyPassword', params.verifyPasswordData));
        yield put({
            type: actions.VERIFY_PASSWORD_SUCCESS,
            payload: 200,
            data: response
        });
    } catch (error) {
        yield put({ type: actions.VERIFY_PASSWORD_FAILURE, message: error?.response?.data });
        return error;
    }
}


export function* userStart(params) {
    let response = '';
    try {
        if (params.id === undefined) {
            response = yield call(() => getRequest('profile'));
        } else {
            response = yield call(() => getRequest(`team/user/${params.id}`));
        }
        yield put({
            type: actions.USER_DETAILS_SUCCESS,
            payload: response.data,
        });
    } catch (error) {
        yield put({ type: actions.USER_DETAILS_FAILURE, message: error });
        return error;
    }
}

export function* changeEmail(params) {
    try {
        const response = yield call(() => postRequest('user/updateProfile', params.changeEmailData));
        yield put({
            type: actions.CHANGE_EMAIL_SUCCESS,
            payload: response.data,
        });
    } catch (error) {
        yield put({ type: actions.CHANGE_EMAIL_FAILURE, message: error?.response?.data });
        return error;
    }
}

export function* updateProfile(params) {
    try {
        const response = yield call(() => postRequest('user/updateProfile', params.updateData));
        yield put({
            type: actions.UPDATE_PROFILE_DETAILS_SUCCESS,
            payload: response.data,
        });
    } catch (error) {
        yield put({ type: actions.UPDATE_PROFILE_DETAILS_FAILURE, message: error?.response?.data });
        return error;
    }
}

export function* forgotPassword(params) {

    try {
        const response = yield call(() => postRequest('user/forgotPassword', params.data));
        yield put({
            type: actions.FORGOT_PASSWORD_SUCCESS,
            payload: response.data,
        });
        storageSetItem('loginToken', response.data.token);
    } catch (error) {
        yield put({ type: actions.FORGOT_PASSWORD_FAILURE, message: error?.response?.data });
        return error;
    }
}

export function* verifyOtp(params) {
    try {
        const response = yield call(() => postRequest('user/verify/otp', params.data));
        yield put({
            type: actions.VERIFY_OTP_SUCCESS,
            payload: "Otp verified successfully",
            data: response
        });
    } catch (error) {
        yield put({ type: actions.VERIFY_OTP_FAILURE, message: error?.response?.data });
        return error;
    }
}

export function* resetPassword(params) {
    try {
        const response = yield call(() => postRequest('user/resetPassword', params.data));
        yield put({
            type: actions.RESET_PASSWORD_SUCCESS,
            payload: "Your Password has been successfully changed",
            data: response
        });
        storageRemoveItem('loginToken');
    } catch (error) {
        yield put({ type: actions.RESET_PASSWORD_FAILURE, message: error?.response?.data });
        return error;
    }
}

export function* getRoleManagement() {
    try {
        let roleId = storageGetItem("roleId");
        const response = yield call(() => getRequest(`role/${roleId}`));
        yield put({
            type: actions.ROLE_MANAGEMENT_SUCCESS,
            payload: response.data,
        });
        // Cache the latest role so permissions survive offline (role/{id} is not an
        // offline-capable endpoint, so the next offline refresh can't reach it).
        try { storageSetItem('roleData', JSON.stringify(response.data)); } catch (e) { /* ignore */ }
    } catch (error) {
        // Offline / fetch failed: restore the last cached role instead of wiping
        // permissions — otherwise the menus disappear and every page redirects with
        // "Access Denied".
        const cached = storageGetItem('roleData');
        if (cached) {
            try {
                yield put({ type: actions.ROLE_MANAGEMENT_SUCCESS, payload: JSON.parse(cached) });
                return;
            } catch (e) { /* fall through to the error path */ }
        }
        yield put({ type: actions.ROLE_MANAGEMENT_ERROR, message: error?.response?.data });
        return error;
    }
}


export function* clearPassword() {
    yield put({
        type: actions.CLEAR_PASSWORD_SUCCESS
    });
}

export function* userLogout() {
    yield put({
        type: actions.LOGOUT_SUCCESS
    });
}

export default function* authSaga() {
    yield all([
        takeEvery(actions.LOGIN_START, login),
        takeEvery(actions.OFFLINE_LOGIN_START, offlineLogin),
        takeEvery(actions.REGISTER_START, register),
        takeEvery(actions.CHANGE_PASSWORD_START, changePassword),
        takeEvery(actions.VERIFY_PASSWORD_START, verifyPassword),
        takeEvery(actions.USER_DETAILS_START, userStart),
        takeEvery(actions.CLEAR_PASSWORD_START, clearPassword),
        takeEvery(actions.CHANGE_EMAIL_START, changeEmail),
        takeEvery(actions.LOGOUT_START, userLogout),
        takeEvery(actions.UPDATE_PROFILE_DETAILS_START, updateProfile),
        takeEvery(actions.FORGOT_PASSWORD_START, forgotPassword),
        takeEvery(actions.VERIFY_OTP_START, verifyOtp),
        takeEvery(actions.ROLE_MANAGEMENT_START, getRoleManagement),
        takeEvery(actions.RESET_PASSWORD_START, resetPassword)
    ]);
}